import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';

import { useAppContext } from '@prisjakt/ui-app-context/src/app-context';

import { Column } from '@prisjakt/ui/list/variants';
import {
  BodySmallText,
  FontEmphasis,
  TitleMediumText,
} from '@prisjakt/ui/text/variants';
import { PrimaryButton } from '@prisjakt/ui/button/variants';

import { EmailHint } from 'src/areas/product-alert/components/product-alert-form/styles';
import { Errors } from 'src/areas/product-alert/components/product-alert-form';
import { CtxLink } from 'src/components/render-context/ctx-link';
import { Modal } from 'src/areas/product/styles';
import { TextInput } from 'src/components/inputs';

import { isValidEmail } from 'src/areas/account/helpers';

import { AppState } from 'src/reducers';

import { StyledLink } from './styles';

type Props = {
  closeModal: () => void;
  isOpen: boolean;
  onSubmit: (
    event: React.FormEvent,
    email?: string,
    onError?: (error?: Errors) => void
  ) => void;
};

export const StockStatusAlertModal = (props: Props) => {
  const { closeModal, isOpen, onSubmit } = props;

  const { market, t } = useAppContext();

  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<Errors | undefined>(undefined);

  const errorState = useSelector(
    (state: AppState) => state.areas.productAlert.error
  );
  const successState = useSelector(
    (state: AppState) => state.areas.productAlert.success
  );

  useEffect(() => {
    if (errorState) {
      switch (errorState) {
        case Errors.ERROR_EMAIL_INVALID:
        case Errors.ERROR_EMAIL_IN_USE: {
          setError(errorState);
          break;
        }
        default: {
          setError(Errors.ERROR_UNKNOWN);
        }
      }
    } else if (successState) {
      setEmail('');
      setError(undefined);
      closeModal();
    }
  }, [closeModal, errorState, successState]);

  const privacyPolicyUri = market?.privacySettings?.responsive?.privacyUrl;

  const handleEmailChange = debounce(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setError(
        isValidEmail(event.target.value)
          ? undefined
          : Errors.ERROR_EMAIL_INVALID
      );
      setEmail(event.target.value);
    },
    500
  );

  const handleSubmit = (event: React.FormEvent) =>
    onSubmit(event, email, setError);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldShowCloseIcon
      width={526}
    >
      <Column gap={1.5}>
        <TitleMediumText>
          {t('product', 'alert.stock-status-alert-email')}
        </TitleMediumText>

        <form method="post" onSubmit={handleSubmit} noValidate>
          <TextInput
            autoFocus
            type="email"
            name="email"
            defaultValue={email}
            onChange={handleEmailChange}
            placeholder={t('alerts', 'alert-form.email-placeholder')}
            tabIndex={0}
            // Do not send this element to CUX (cux.io)
            data-cux-exclude
            variant="rounded"
          />
          <EmailHint visible={error === Errors.ERROR_EMAIL_IN_USE}>
            {t('alerts', 'alert-form.email-in-use-error')}
            &nbsp;
            <CtxLink to="/login">
              {t('alerts', 'alert-form.login-error')}
            </CtxLink>
          </EmailHint>
          <EmailHint error visible={error === Errors.ERROR_EMAIL_INVALID}>
            {t('alerts', 'alert-form.invalid-email-error')}
          </EmailHint>
          {privacyPolicyUri && (
            <StyledLink>
              <CtxLink to={privacyPolicyUri}>
                <BodySmallText emphasis={FontEmphasis.MEDIUM}>
                  {t('product', 'alert.how-your-email-is-used')}
                </BodySmallText>
              </CtxLink>
            </StyledLink>
          )}

          <PrimaryButton disabled={Boolean(error)} htmlType="submit" fullWidth>
            {t('product', 'alert.create-stock-status-alert')}
          </PrimaryButton>
        </form>
      </Column>
    </Modal>
  );
};
